import { useIntl } from '@capsicum/common';
import { useRouter } from '@capsicum/common-web';
import { type BidderRowProps, type LatestBidListProps } from '@capsicum/features/fanshop/shared';
import { BidderRow } from '@capsicum/features/fanshop/web';
import { useCallback } from 'react';
import styled from 'styled-components';

export const LatestBidList = ({
	auctionId,
	bidParticipants,
	chzCurrencyShortCode,
	chzRate,
	fiatCurrencyShortCode
}: LatestBidListProps) => {
	const { $t } = useIntl();
	const router = useRouter();

	const BidList = useCallback(
		() => (
			<div>
				{bidParticipants?.map(
					(
						bidUser: Omit<BidderRowProps, 'chzCurrencyShortCode' | 'chzRate' | 'fiatCurrencyShortCode'>,
						index: number
					) =>
						(bidUser.status === 'accepted' ||
							bidUser.status === 'outbidded' ||
							bidUser.status === 'validated') &&
						index < 3 && (
							<S.StyledBidderRowWrapper key={`${bidUser?.amount}-${index}`}>
								<BidderRow
									amount={bidUser?.amount ? bidUser.amount : 0}
									amountChz={bidUser?.amountChz ? bidUser.amountChz : 0}
									chzCurrencyShortCode={chzCurrencyShortCode}
									date={bidUser?.date}
									fiatCurrencyShortCode={fiatCurrencyShortCode}
									iso2Country={bidUser?.iso2Country}
									isOwnBid={bidUser?.isOwnBid}
									mwsUserId={bidUser?.mwsUserId}
								/>
							</S.StyledBidderRowWrapper>
						)
				)}
			</div>
		),
		[bidParticipants, chzRate, auctionId, chzCurrencyShortCode, chzCurrencyShortCode]
	);

	return (
		<div>
			<S.TitleWrapper>
				<S.Title>{$t('rewards.auctions.bids.latestBids')}</S.Title>
				{(bidParticipants?.length || 0) > 3 && (
					<S.SeeAllButton
						data-testid="see-all-bid-history"
						onClick={() => router.push(`/fan-shop/rewards/auction/${auctionId}/bid-history`)}
					>
						{$t('global.seeAll')}
					</S.SeeAllButton>
				)}
			</S.TitleWrapper>

			<BidList />
		</div>
	);
};

namespace S {
	export const TitleWrapper = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Title = styled.div`
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const SeeAllButton = styled.button`
		${({ theme }) => theme.fonts.text.text1};
		margin: ${({ theme }) => `${theme.spacings.xxxsmall} 0 0 ${theme.spacings.small}`};
		padding: 0;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		text-decoration: none;
		border: none;
		background: ${({ theme }) => theme.colors.backgrounds.transparent};
		flex-shrink: 0;
	`;

	export const StyledBidderRowWrapper = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
